import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Button, EmptyState, SidebarHandle, Spinner, ThemeProvider, ToastProvider, navigate, tabAccount, useLocation, useSidebar } from '@dugzi/ui';
import { APP, api } from './lib/api';
import { DataProvider } from './lib/data';
import type { Me } from './lib/types';
import { TopBar } from './components/TopBar';
import { Sidebar } from './components/Sidebar';
import { PeopleView } from './components/PeopleView';

const Birthdays = lazy(() => import('./components/Birthdays'));
const Duplicates = lazy(() => import('./components/Duplicates'));
const Trash = lazy(() => import('./components/Trash'));
const Households = lazy(() => import('./components/Households'));
const ImportExport = lazy(() => import('./components/ImportExport'));

export type Route =
  | { kind: 'all' }
  | { kind: 'book'; id: string }
  | { kind: 'label'; label: string }
  | { kind: 'household'; id: string }
  | { kind: 'birthdays' }
  | { kind: 'duplicates' }
  | { kind: 'trash' }
  | { kind: 'households' };

export function parseRoute(path: string): Route {
  let m = /^\/b\/([^/]+)/.exec(path);
  if (m) return { kind: 'book', id: m[1] };
  m = /^\/l\/([^/]+)/.exec(path);
  if (m) return { kind: 'label', label: decodeURIComponent(m[1]) };
  m = /^\/h\/([^/]+)/.exec(path);
  if (m) return { kind: 'household', id: m[1] };
  if (path === '/birthdays') return { kind: 'birthdays' };
  if (path === '/duplicates') return { kind: 'duplicates' };
  if (path === '/trash') return { kind: 'trash' };
  if (path === '/households') return { kind: 'households' };
  return { kind: 'all' };
}

/** Dugzi Contacts on its own (D-031, D-034): works with any Dugzi Account, any email address. */
export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const signinFailed = new URLSearchParams(location.search).get('signin') === 'failed';
  useEffect(() => {
    if (signinFailed) return;
    tabAccount.adoptFromUrl(APP);
    api<Me>('/api/me').then(setMe).catch((e) => { if (e.status !== 401) setFailed(e.message); });
  }, [signinFailed]);
  const initial = useMemo(() => (me ? { appearance: me.user.appearance, accent: me.user.accent } : null), [me?.user.appearance, me?.user.accent]); // eslint-disable-line react-hooks/exhaustive-deps
  if (signinFailed) {
    return (
      <main className="boot" data-testid="signin-failed">
        <EmptyState title="Sign-in didn’t finish" action={<Button variant="primary" onClick={() => location.assign('/api/auth/login')}>Try again</Button>}>
          Something interrupted signing in to Dugzi Contacts. Nothing was changed.
        </EmptyState>
      </main>
    );
  }
  if (failed) return <main className="boot"><EmptyState title="We couldn't open your contacts" action={<Button onClick={() => location.reload()}>Try again</Button>}>{failed}</EmptyState></main>;
  if (!me) return <div className="boot"><Spinner label="Opening Dugzi Contacts" /></div>;
  return (
    <ThemeProvider initial={initial}>
      <ToastProvider>
        <DataProvider me={me}>
          <Shell me={me} />
        </DataProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

function Shell({ me }: { me: Me }) {
  const { path, search } = useLocation();
  const route = parseRoute(path);
  const [navOpen, setNavOpen] = useState(false);
  const [moving, setMoving] = useState<null | 'import' | 'export' | 'sync'>(() => new URLSearchParams(search).get('import') === '1' ? 'import' : null);
  useEffect(() => { setNavOpen(false); }, [path]);
  // Keyboard: "/" searches, "n" adds someone (not while typing).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('input, textarea, select, [contenteditable="true"], [role="combobox"], dialog') || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '/') { e.preventDefault(); document.querySelector<HTMLInputElement>('[data-testid="search"]')?.focus(); }
      if (e.key === 'n') { e.preventDefault(); const q = new URLSearchParams(search); q.delete('p'); q.set('new', '1'); navigate(`${path}?${q}`); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [path, search]);
  const sidebar = useSidebar('contacts', 'lists', { defaultWidth: 260, min: 200, max: 420, narrowQuery: '(max-width: 860px)' });
  return (
    <div className="app">
      <TopBar me={me} onMenu={() => setNavOpen((v) => !v)} onMove={setMoving} />
      <div className="app__body" {...sidebar.hostProps}>
        <SidebarHandle sb={sidebar} label="Resize sidebar" />
        <Sidebar route={route} open={navOpen} onClose={() => setNavOpen(false)} onMove={setMoving} />
        <main id="main" className="main" tabIndex={-1}>
          <Suspense fallback={<div className="view__loading"><Spinner label="Loading" /></div>}>
            {route.kind === 'birthdays' ? <Birthdays />
              : route.kind === 'duplicates' ? <Duplicates />
              : route.kind === 'trash' ? <Trash />
              : route.kind === 'households' ? <Households />
              : <PeopleView key={JSON.stringify(route)} route={route} />}
          </Suspense>
        </main>
      </div>
      <Suspense fallback={null}>{moving && <ImportExport tab={moving} onClose={() => setMoving(null)} />}</Suspense>
    </div>
  );
}
